import type { MatomoTracker } from 'src/types/matomo';

import { useEffect } from 'react';
import { useLocation } from 'react-router';

/**
 * Custom hook to track page views with Matomo
 * Automatically tracks route changes in React Router
 */
export function useMatomo() {
  const location = useLocation();

  useEffect(() => {
    // Check if Matomo is loaded
    if (typeof window !== 'undefined' && window._paq) {
      const _paq: MatomoTracker = window._paq;

      // Track page view for the current route
      _paq.push(['setCustomUrl', window.location.href]);
      _paq.push(['setDocumentTitle', document.title]);
      _paq.push(['trackPageView']);

      // Track any link clicks
      _paq.push(['enableLinkTracking']);
    }
  }, [location]);
}

/**
 * Track custom events with Matomo
 * @param category - The event category
 * @param action - The event action
 * @param name - Optional event name
 * @param value - Optional numeric value
 */
export function trackMatomoEvent(category: string, action: string, name?: string, value?: number) {
  if (typeof window !== 'undefined' && window._paq) {
    const _paq: MatomoTracker = window._paq;
    const eventData: any[] = ['trackEvent', category, action];

    if (name) eventData.push(name);
    if (value !== undefined) eventData.push(value);

    _paq.push(eventData);
  }
}

/**
 * Track site search with Matomo
 * @param keyword - The search keyword
 * @param category - Optional search category
 * @param resultsCount - Optional number of results
 */
export function trackMatomoSiteSearch(keyword: string, category?: string, resultsCount?: number) {
  if (typeof window !== 'undefined' && window._paq) {
    const _paq: MatomoTracker = window._paq;
    _paq.push(['trackSiteSearch', keyword, category || false, resultsCount || false]);
  }
}
